/**
 * Centralized Error Logging System
 * 
 * Usage:
 *   import { logError, ErrorType, Severity } from '@/lib/errorLogging';
 *   
 *   // Log an error
 *   logError({
 *     type: 'frontend',
 *     source: 'AgentDetail',
 *     message: 'Failed to load agent',
 *     error: err,
 *     agentId: agent.id
 *   });
 */

import { supabase } from '@/integrations/supabase/client';

export type ErrorType = 'frontend' | 'edge_function' | 'worker' | 'provisioning' | 'webhook' | 'unknown';
export type Severity = 'debug' | 'info' | 'warning' | 'error' | 'critical';

export interface LogErrorParams {
  type: ErrorType;
  source: string;
  message: string;
  error?: Error | unknown;
  errorCode?: string;
  severity?: Severity;
  agentId?: string;
  organizationId?: string;
  requestPath?: string;
  requestMethod?: string;
  requestBody?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

/**
 * Log an error to the database
 */
export async function logError(params: LogErrorParams): Promise<string | null> {
  const {
    type,
    source,
    message,
    error,
    errorCode,
    severity = 'error',
    agentId,
    organizationId,
    requestPath,
    requestMethod,
    requestBody,
    metadata = {}
  } = params;

  // Extract stack trace from error
  let stackTrace: string | undefined;
  let errorMessage = message;
  
  if (error instanceof Error) {
    stackTrace = error.stack;
    if (!message) {
      errorMessage = error.message;
    }
  } else if (error) {
    try {
      errorMessage = message || String(error);
      metadata['rawError'] = error;
    } catch {
      // Ignore serialization errors
    }
  }

  // Get current user
  const { data: { user } } = await supabase.auth.getUser();

  // Add browser/environment info to metadata
  const enrichedMetadata = {
    ...metadata,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    timestamp: new Date().toISOString(),
  };

  try {
    const { data, error: dbError } = await supabase.rpc('log_error', {
      p_error_type: type,
      p_source: source,
      p_message: errorMessage,
      p_stack_trace: stackTrace || null,
      p_error_code: errorCode || null,
      p_severity: severity,
      p_user_id: user?.id || null,
      p_agent_id: agentId || null,
      p_organization_id: organizationId || null,
      p_request_path: requestPath || null,
      p_request_method: requestMethod || null,
      p_request_body: (requestBody || null) as any,
      p_metadata: enrichedMetadata as any
    });

    if (dbError) {
      console.error('[ErrorLogging] Failed to log error to database:', dbError);
      return null;
    }

    return data as string;
  } catch (err) {
    console.error('[ErrorLogging] Exception while logging error:', err);
    return null;
  }
}

/**
 * Wrap an async function with error logging
 */
export function withErrorLogging<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  source: string,
  options: Partial<LogErrorParams> = {}
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      await logError({
        type: 'frontend',
        source,
        message: error instanceof Error ? error.message : 'Unknown error',
        error,
        ...options
      });
      throw error;
    }
  }) as T;
}

/**
 * Log API/fetch errors with request context
 */
export async function logApiError(
  source: string,
  error: Error | unknown,
  requestInfo: {
    path: string;
    method: string;
    body?: Record<string, unknown>;
    response?: Response;
  },
  agentId?: string
): Promise<void> {
  let responseBody: string | undefined;
  try {
    if (requestInfo.response) {
      responseBody = await requestInfo.response.clone().text();
    }
  } catch {
    // Ignore
  }

  await logError({
    type: 'frontend',
    source,
    message: error instanceof Error ? error.message : 'API request failed',
    error,
    severity: 'error',
    agentId,
    requestPath: requestInfo.path,
    requestMethod: requestInfo.method,
    requestBody: requestInfo.body,
    metadata: {
      responseStatus: requestInfo.response?.status,
      responseBody: responseBody?.substring(0, 1000) // Limit size
    }
  });
}

/**
 * Initialize global error handlers
 * Call this once in your app entry point (main.tsx)
 */
export function initGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  // Handle uncaught errors
  window.onerror = (message, source, lineno, colno, error) => {
    logError({
      type: 'frontend',
      source: 'window.onerror',
      message: typeof message === 'string' ? message : 'Unknown error',
      error,
      severity: 'critical',
      metadata: {
        sourceFile: source,
        line: lineno,
        column: colno
      }
    });
    return false; // Don't prevent default handling
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = (event) => {
    logError({
      type: 'frontend',
      source: 'unhandledrejection',
      message: event.reason?.message || 'Unhandled promise rejection',
      error: event.reason,
      severity: 'critical',
      metadata: {
        reason: event.reason
      }
    });
  };

  console.log('[ErrorLogging] Global error handlers initialized');
}

// Error codes for consistent categorization
export const ErrorCodes = {
  // Frontend errors (FE_xxx)
  FE_RENDER: 'FE_001',
  FE_API_CALL: 'FE_002',
  FE_AUTH: 'FE_003',
  FE_VALIDATION: 'FE_004',
  FE_NAVIGATION: 'FE_005',
  
  // Provisioning errors (PROV_xxx)
  PROV_VPS_CREATE: 'PROV_001',
  PROV_VPS_CONFIGURE: 'PROV_002',
  PROV_CLAWDBOT_INSTALL: 'PROV_003',
  PROV_CLAWDBOT_CONFIG: 'PROV_004',
  PROV_TIMEOUT: 'PROV_005',
  
  // Edge function errors (EDGE_xxx)
  EDGE_AUTH: 'EDGE_001',
  EDGE_VALIDATION: 'EDGE_002',
  EDGE_EXTERNAL_API: 'EDGE_003',
  EDGE_DATABASE: 'EDGE_004',
  EDGE_WORKER_COMM: 'EDGE_005',
  
  // Worker errors (WORK_xxx)
  WORK_COMMAND: 'WORK_001',
  WORK_SSH: 'WORK_002',
  WORK_TIMEOUT: 'WORK_003',
  WORK_RESOURCE: 'WORK_004',
  
  // Webhook errors (HOOK_xxx)
  HOOK_STRIPE: 'HOOK_001',
  HOOK_TELEGRAM: 'HOOK_002',
  HOOK_SIGNATURE: 'HOOK_003',
} as const;
